import React, { useState } from 'react';
import { X, Lock, CheckCircle, AlertCircle } from 'lucide-react';

const EnrollmentCodeModal = ({ isOpen, onClose, onValidate, labName }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleCodeChange = (e) => {
    // Format code as XXX-XXX-XXX
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    
    if (value.length > 3 && value.length <= 6) {
      value = value.slice(0, 3) + '-' + value.slice(3);
    } else if (value.length > 6) {
      value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 9);
    }
    
    setCode(value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (code.length < 11) { // XXX-XXX-XXX = 11 characters
      setError('Please enter a complete enrollment code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onValidate(code);
      setCode('');
    } catch (err) {
      setError(err.message || 'Invalid enrollment code');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError('');
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button style={styles.closeButton} onClick={handleClose}>
          <X size={24} color="#9ca3af" />
        </button>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.iconCircle}>
            <Lock size={32} color="#3b82f6" />
          </div>
          <h2 style={styles.title}>Enter Enrollment Code</h2>
          <p style={styles.subtitle}>
            Enter your unique enrollment code to access <strong>{labName}</strong>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Enrollment Code</label>
            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="ABC-123-XYZ"
              maxLength={11}
              style={{
                ...styles.input,
                ...(error ? styles.inputError : {})
              }}
              autoFocus
            />
            <p style={styles.hint}>Format: XXX-XXX-XXX (letters and numbers)</p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={styles.errorBox}>
              <AlertCircle size={18} color="#ef4444" />
              <span style={styles.errorText}>{error}</span>
            </div>
          )}

          {/* Info Box */}
          <div style={styles.infoBox}>
            <CheckCircle size={18} color="#10b981" />
            <div style={styles.infoText}>
              <p style={styles.infoTitle}>Where to find your code?</p>
              <p style={styles.infoDesc}>
                Your enrollment code was sent to you when you enrolled in the course. 
                Check your email or student portal for your unique code.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <button
              type="button"
              onClick={handleClose}
              style={styles.cancelButton}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...(loading ? styles.submitButtonLoading : {})
              }}
              disabled={loading || code.length < 11}
            >
              {loading ? (
                <>
                  <span style={styles.spinner}></span>
                  Validating...
                </>
              ) : (
                'Access Lab'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#1e293b',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
  },
  closeButton: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    transition: 'background 0.2s',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  iconCircle: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: '12px'
  },
  subtitle: {
    fontSize: '16px',
    color: '#94a3b8',
    lineHeight: '1.5'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#e2e8f0'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '18px',
    fontWeight: '600',
    letterSpacing: '2px',
    textAlign: 'center',
    backgroundColor: '#0f172a',
    border: '2px solid #334155',
    borderRadius: '8px',
    color: '#ffffff',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'monospace'
  },
  inputError: {
    borderColor: '#ef4444'
  },
  hint: {
    fontSize: '12px',
    color: '#64748b',
    textAlign: 'center'
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px'
  },
  errorText: {
    fontSize: '14px',
    color: '#ef4444'
  },
  infoBox: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    borderRadius: '8px'
  },
  infoText: {
    flex: 1
  },
  infoTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#10b981',
    marginBottom: '4px'
  },
  infoDesc: {
    fontSize: '13px',
    color: '#94a3b8',
    lineHeight: '1.5'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px'
  },
  cancelButton: {
    flex: 1,
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#94a3b8',
    backgroundColor: '#334155',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  submitButton: {
    flex: 1,
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  submitButtonLoading: {
    opacity: 0.7,
    cursor: 'not-allowed'
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }
};

// Add spinner animation to head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

export default EnrollmentCodeModal;
